import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { AdminClientTariffService } from '@/shared/api'
import type { ClientTariffWriteRequest } from '@/shared/api'

const KEYS = {
  list: (params?: unknown) => ['client-tariffs', 'list', params],
  details: (id: number) => ['client-tariffs', 'details', id],
}

export function useClientTariffs(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => AdminClientTariffService.list(params),
    staleTime: 60000,
  })
}

export function useClientTariff(id: number) {
  return useQuery({
    queryKey: KEYS.details(id),
    queryFn: () => AdminClientTariffService.details(id),
    enabled: id > 0,
  })
}

export function useCreateClientTariff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ClientTariffWriteRequest) => AdminClientTariffService.create(body),
    onSuccess: () => {
      toast.success('Тариф создан')
      qc.invalidateQueries({ queryKey: ['client-tariffs'] })
    },
    onError: (e: { alreadyToasted?: boolean; detail?: string }) => {
      if (!e?.alreadyToasted) toast.error(e?.detail || 'Ошибка создания тарифа')
    },
  })
}

export function useUpdateClientTariff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ClientTariffWriteRequest }) =>
      AdminClientTariffService.update(id, body),
    onSuccess: () => {
      toast.success('Тариф сохранён')
      qc.invalidateQueries({ queryKey: ['client-tariffs'] })
    },
    onError: (e: { alreadyToasted?: boolean; detail?: string }) => {
      if (!e?.alreadyToasted) toast.error(e?.detail || 'Ошибка сохранения тарифа')
    },
  })
}

export function useSetClientTariffActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      AdminClientTariffService.setActive(id, active),
    onSuccess: (_r, v) => {
      toast.success(v.active ? 'Тариф активирован' : 'Тариф приостановлен')
      qc.invalidateQueries({ queryKey: ['client-tariffs'] })
    },
    onError: (e: { alreadyToasted?: boolean; detail?: string }) => {
      if (!e?.alreadyToasted) toast.error(e?.detail || 'Ошибка смены статуса')
    },
  })
}

export function useDeleteClientTariff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => AdminClientTariffService.remove(id),
    onSuccess: () => {
      toast.success('Тариф удалён')
      qc.invalidateQueries({ queryKey: ['client-tariffs'] })
    },
    onError: (e: { alreadyToasted?: boolean; detail?: string }) => {
      if (!e?.alreadyToasted) toast.error(e?.detail || 'Ошибка удаления тарифа')
    },
  })
}
